import { Field } from '../types/map.types';
import { GridFilterModel } from '@mui/x-data-grid';
import { removeInvalidEntries } from '../utils/grid';
import { useFilters } from '../../useFilters';
import { useUnits } from '@innovyze/stylovyze';
import { useMemo } from 'react';
import { removeExcludedFilter } from '../../../utils/filter';

type UpdateModel = (model: GridFilterModel) => void;

const getUnitForField = (columns: Field[], fieldKey: string) => {
	return columns.find(field => field.fieldKey === fieldKey)?.units;
};

export const useFilterModel = (
	columns: Field[],
): [GridFilterModel, UpdateModel, GridFilterModel, UpdateModel] => {
	const { rawFilter, setFilterModel } = useFilters();
	const {
		system,
		getStandardisedValue,
		getSystemValue,
		getSystemUnit,
	} = useUnits();

	const convertModel = (model: GridFilterModel): GridFilterModel => {
		if (system === 'Metric' || !model) {
			return model;
		}
		const items = model.items.map(item => {
			const unit = getUnitForField(columns, item.field);
			if (unit && item.value) {
				const systemUnit = getSystemUnit(unit);
				if (systemUnit) {
					const value = getStandardisedValue(
						`${item.value}${systemUnit}`,
						item.value, // <-- keep value if conversion fails
						unit,
					);
					return { ...item, value };
				}
			}
			return item;
		});
		return {
			...model,
			items,
		};
	};

	// Sets a grid filter model loaded from server backend.
	// Note: MongoDb uses Metric, so convert only filter items with Imperial units
	const setFilterFromServer = (model: GridFilterModel) => {
		if (system === 'Metric' || !model?.items?.length) {
			setFilterModel(model);
		} else {
			const convertedItems = model.items.map(item => {
				let value = item.value;
				const unit = getUnitForField(columns, item.field);
				if (unit && value) {
					value = getSystemValue(`${value}${unit}`);
				}
				return { ...item, value };
			});
			setFilterModel({ ...model, items: convertedItems });
		}
	};

	const processFilters = (model: GridFilterModel) => {
		const updatedModel = model ? removeInvalidEntries(model) : model;
		if (updatedModel?.items?.length) {
			const convertedModel = convertModel(updatedModel);
			return convertedModel;
		} else {
			return updatedModel;
		}
	};

	// `columns` is intentionally omitted from deps: it's only needed for unit
	// conversion inside processFilters (non-Metric paths), and including it
	// caused the memo to recompute on every schema-load, producing a new object
	// reference that cascaded into a "Maximum update depth exceeded" loop via
	// the visibility useEffect in AssetGrid.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const filterForServer = useMemo(
		() => processFilters(removeExcludedFilter(rawFilter)),
		[rawFilter, system],
	);

	return [rawFilter, setFilterModel, filterForServer, setFilterFromServer];
};
